/* eslint-disable array-callback-return */
"use client";
import { useEffect, useState, useRef } from "react";
import PropTypes from "prop-types";
import { useSelector } from "react-redux";

import { getPoisByTownId, getPolygonBounds } from "services/location";
import { urlMaker } from "@/src/services/url-maker";

import { getQueryParameterFromUrl, addParameterToURL } from "src/utils/url";
import {
  getListingParams,
  getPlacemarkClass,
  createBalloons,
  getIconContent,
  createPlacemark,
  getMarkerContent,
  getPlacemarkHoverStyle,
  getPlacemarkNormalStyle,
  getNewCriteria,
  decideLocationToFocus,
  removeBottomLeftTopRightFromPath,
  debounce,
} from "./helpers";
import {
  normalizePolygons,
  getMapOptions,
  isPolygonActive,
  reduceDuplicates,
  isSelectedLocationsParent,
  removeParentLocations,
} from "src/utils/map";
import { poiTypes } from "src/utils/constants/poi-types";
import { getNodeLocations } from "@/src/utils/conversion";
import { allListingsParams, initialBounds, initialMapCenter } from "./constants";
import PoiButton from "components/listing-map/poi-button";
import Loading from "./loading";
import MapSwitchButton from "src/components/listing-search/map-switch-button";
import ListingCount from "../listing-count";

import styles from "./styles.module.css";

const ListingMap = ({ handlePlacemarkClick, getAllListings, newSearchResults, isProjectPage }) => {
  const { total = 0, searchCriteria = {}, url } = newSearchResults || {};
  const allListings = newSearchResults?.allListings || [];
  const initialLocations = getNodeLocations(searchCriteria);
  const prevLocation = initialLocations?.map((location) => location.name);
  const prevLocationRef = useRef(prevLocation);
  const boundsChangeRef = useRef(false);
  const polygonClickedRef = useRef(false);
  const locations = getNodeLocations(searchCriteria);
  const selectedLocationId = locations[0]?.id;
  const activePoiCategories = [7, 5, 2, 8, 11];
  const visiblePoiTypes = poiTypes.filter((poiType) => activePoiCategories.indexOf(poiType.id) > -1);

  const hoveredListingId = useSelector(({ hoveredListing }) => hoveredListing.sidebarHoveredListing);

  const [loading, setLoading] = useState(true);
  const [placemarks, setPlacemarks] = useState([]);
  const [filteredPOIs, setFilteredPOIs] = useState([]);
  const [selectedPoiTypeIds, setSelectedPoiTypeIds] = useState([]);
  const [currentPins, setCurrentPins] = useState([]);
  const [showPoi, setShowPoi] = useState(false);
  const [selectedRegion, setSelectedRegion] = useState(null);
  const mapRef = useRef(null);

  useEffect(() => {
    initializeMapScript();
  }, []);

  useEffect(() => {
    if (window.map) {
      fetchListingsSubFunction(searchCriteria);
    }
  }, [selectedLocationId]);

  useEffect(() => {
    if (window.map) handleSidebarListingHover(hoveredListingId, window.map);
  }, [hoveredListingId]);

  useEffect(() => {
    return () => {
      if (window.map) {
        window.map.events.removeAll();
        window.map.destroy();
        window.map = null;
      }
    };
  }, []);

  const initializeMapScript = () => {
    const mapApiKey = window.env.EJ_YANDEX_API_KEY;

    if (!document.getElementById("yandex-map-script")) {
      const script = document.createElement("script");
      script.id = "yandex-map-script";
      script.type = "text/javascript";
      script.src = `https://api-maps.yandex.ru/2.1/?apikey=${mapApiKey}&lang=tr_TR`;

      script.onload = () => {
        window.ymaps.ready(onScriptLoad);
      };

      document.head.appendChild(script);
    } else {
      if (window.ymaps && window.ymaps.ready) {
        window.ymaps.ready(onScriptLoad);
      }
    }
  };

  const onScriptLoad = () => {
    const { bottomLeft, topRight } = initialBounds;

    window.ymaps.ready(() => {
      window.map = new window.ymaps.Map("listingmap", {
        center: initialMapCenter,
        zoom: 12,
        controls: [],
      });

      const scrollZoom = window.map.behaviors.get("scrollZoom");
      scrollZoom.options.set({ speed: 5, duration: 300, smoothZoom: true });

      const locations = getNodeLocations(searchCriteria);
      const hasLocations = locations?.length > 0;
      const region = searchCriteria?.region;

      if (region) {
        focusCenterOfSelectedPolygons(region?.bounds);
      } else if (hasLocations) {
        setPreloadLocation(locations);
      } else {
        window.map.setBounds([bottomLeft, topRight]);
      }

      const zoomControl = new window.ymaps.control.ZoomControl({
        options: {
          size: "small",
          position: {
            position: "absolute",
            right: 20,
            top: 100,
            zIndex: 10000,
          },
        },
      });

      window.ymaps.option.presetStorage.add("mark-icon", {
        iconLayout: "default#image",
        iconImageHref: "/assets/icons/mark-icon.svg",
        iconImageSize: [13, 13],
        iconImageOffset: [0, 0],
      });

      window.map.events.add("boundschange", (event) => {
        const newBounds = event.get("newBounds");
        debouncedHandleMapBoundsChange(newBounds, searchCriteria);
      });

      onMapReady(searchCriteria);

      window.map.controls.add(zoomControl);
    });
  };

  const rePlaceMarkers = (map) => {
    if (!map) return;
    removeMarkers(map);
    placeMarkers(map);
  };

  const setPreloadLocation = (currentLocations) => {
    const lat = currentLocations?.[0]?.center?.lat || currentLocations?.[0]?.center?.[1] || initialMapCenter[0];
    const lon = currentLocations?.[0]?.center?.lon || currentLocations?.[0]?.center?.[0] || initialMapCenter[1];
    window.map.setCenter([lat, lon], 12);
  };

  const onMapReady = (searchCriteria) => {
    const locations = getNodeLocations(searchCriteria);
    if (locations?.length === 1 && locations[0]?.type === "town") {
      setShowPoi(true);
    } else {
      setShowPoi(false);
    }

    if (locations?.length > 0) {
      const newLocations = locations.filter((location) => location.type !== "project");
      setCurrentLocationPolygons(window.map, newLocations);
    } else {
      setInitialLocation(window.map);
    }
    if (window.map) {
      rePlaceMarkers(window.map);
    }
  };

  const handleSidebarListingHover = async (hoveredItemId, map) => {
    let foundPlacemark = null;
    await map.geoObjects.each((geoObject) => {
      if (geoObject.properties.get("id") === hoveredItemId) {
        foundPlacemark = geoObject;
      }
    });
    if (foundPlacemark) {
      foundPlacemark.balloon.open();
    } else {
      map.geoObjects.each((geoObject) => {
        if (geoObject?.balloon?.isOpen()) {
          geoObject.balloon.close();
        }
      });
    }
  };

  const setCurrentLocationPolygons = async (map, currentLocations) => {
    if (boundsChangeRef.current) return;
    const isRegionSelected = currentLocations.some((location) => location.type === "region");
    const selectedDistrictCount =
      currentLocations.filter((currentLocation) => currentLocation.type === "district").length || 0;
    const selectedTown = currentLocations.filter((location) => location.type === "town");
    const locationIds =
      selectedTown.length > 0 && selectedDistrictCount <= 1
        ? selectedTown.map((l) => l.id)
        : currentLocations.map((l) => l.id);
    try {
      const res = await getPolygonBounds(locationIds.join(","), isRegionSelected);
      if (res?.locations) {
        placePolygons(map, res?.locations, currentLocations);
        const fallbackCenter = currentLocations[0]?.center;
        if (fallbackCenter?.lat && fallbackCenter?.lon) {
          window.map.setCenter([fallbackCenter.lat, fallbackCenter.lon]);
        }
      }
    } catch (error) {
      window.map.setCenter([currentLocations[0]?.center?.lat, currentLocations[0]?.center?.lon]);
      setLoading(false);
      console.error("setCurrentLocationPolygons -> Error:", error);
    }
  };

  const setInitialLocation = async (map) => {
    try {
      await focusCenterOfSelectedPolygons(initialBounds);
      const res = await getPolygonBounds();
      if (res?.locations) {
        placePolygons(map, res?.locations);
      } else {
        placePolygons(map, []);
        console.warn("getPolygonBounds -> No locations found in response");
      }
    } catch (error) {
      placePolygons(map, []);
      console.error("getPolygonBounds -> Error in setInitialLocation:", error.message);
    }
  };

  const placePolygons = (map, locations, selectedLocations) => {
    //! ! this feature is removed for now, will be improved later
    setLoading(false);

    // let activeLocationTypes = [];
    // if (selectedLocations) {
    //   activeLocationTypes = reduceDuplicates(selectedLocations.map((c) => c.type));
    // }

    // const allRegions = locations.filter((location) => location.type === "region");
    // const onlyRegions = allRegions.length === 7;

    // const geoCollection = new window.ymaps.GeoObjectCollection();

    // locations.forEach((location) => {
    //   const options = getMapOptions(location.type);
    //   const splitedObj = options.coordinatesField?.split(".");
    //   const compressedPolylines = location[splitedObj[0]]?.[splitedObj[1]];
    //   const coordinates = normalizePolygons(compressedPolylines);
    //   const isActive = selectedLocations ? isPolygonActive(location, selectedLocations, activeLocationTypes) : false;

    //   if (!isSelectedLocationsParent(location, selectedLocations, activeLocationTypes) || onlyRegions) {
    //     coordinates.forEach((coordinate) => {
    //       const polygon = new window.ymaps.Polygon(
    //         [coordinate],
    //         { hintContent: location.name },
    //         {
    //           fillColor: isActive || onlyRegions ? "#FFFFFF" : "#5F787E",
    //           strokeColor: "#5F787E",
    //           fillOpacity: 0.2,
    //           strokeOpacity: 0.8,
    //         }
    //       );

    //       polygon.events.add(["click"], () => {
    //         polygonClickedRef.current = true;
    //         // setLoading(true);
    //         const bottomLeft = location?.bounds.bottomLeft;
    //         const topRight = location?.bounds.topRight;
    //         if (prevLocationRef.current.includes(location.name)) return;
    //         map.setBounds([bottomLeft.reverse(), topRight.reverse()]).then(
    //           () => {
    //             const locationNames = [location.name];
    //             prevLocationRef.current = locationNames;
    //             setNewLocation(location, map);
    //           },
    //           (error) => {
    //             console.error("Bound change error!", error);
    //           }
    //         );
    //       });

    //       polygon.events.add(["mouseenter"], () => {
    //         polygon.options.set("fillOpacity", 0.4);
    //         if (isActive || onlyRegions) {
    //           polygon.options.set("fillColor", "#5F787E");
    //         }
    //       });

    //       polygon.events.add(["mouseleave"], () => {
    //         polygon.options.set("fillOpacity", 0.2);
    //         if (isActive || onlyRegions) {
    //           polygon.options.set("fillColor", "#fff");
    //         }
    //       });

    //       // geoCollection.add(polygon);
    //     });
    //   }
    // });

    // // console.time("geoObjectsAdd");
    // // const start = performance.now();
    // map.geoObjects.add(geoCollection);
    // // console.timeEnd("geoObjectsAdd");
    // // const end = performance.now();
    // // console.log(`geoObjects.add süresi: ${end - start} ms`);
    // // const durationMs = end - start;
    // // const durationSec = durationMs / 1000;
    // // console.log(`geoObjects.add süresi: ${durationMs.toFixed(2)} ms (${durationSec.toFixed(2)} s)`);

    // setLoading(false);
  };

  const getListingsAndUpdateUrl = async (url) => {
    const res = await getAllListings(url, {
      ...allListingsParams,
      ...(isProjectPage
        ? { isProjectSearch: true, isListingSearch: false }
        : { isProjectSearch: false, isListingSearch: true }),
    });
    await removeMarkers(window.map);
    if (boundsChangeRef.current) {
      placeMarkers(window.map, res?.allListings);
    }
    let urlToRedirect = url;
    if (!getQueryParameterFromUrl("view_type", urlToRedirect)) {
      urlToRedirect = addParameterToURL("view_type=map", urlToRedirect);
    }
    window.history.pushState({}, document.title, urlToRedirect);
  };

  const setNewLocation = async (location) => {
    polygonClickedRef.current = true;
    boundsChangeRef.current = false;
    const { type, bounds } = location;
    const hasTownSelected = type === "town";
    if (hasTownSelected) {
      setShowPoi(true);
    } else {
      setShowPoi(false);
    }
    const hasRegionSelected = type === "region";
    if (hasRegionSelected) {
      setSelectedRegion(location);
    } else {
      setSelectedRegion(null);
    }
    focusCenterOfSelectedPolygons(bounds);
    const newCriteria = await getNewCriteria(location, searchCriteria);
    const res = await urlMaker(newCriteria, "maps");
    const newPath = removeBottomLeftTopRightFromPath(res.path);
    if (newPath) {
      getListingsAndUpdateUrl(newPath);
    }
  };

  const handleMapBoundsChange = async (bounds, searchCriteria) => {
    boundsChangeRef.current = true;
    if (polygonClickedRef.current) {
      polygonClickedRef.current = false;
      return;
    }
    const map = {
      bottomLeftLat: bounds?.[0]?.[0] ?? null,
      bottomLeftLon: bounds?.[0]?.[1] ?? null,
      topRightLat: bounds?.[1]?.[0] ?? null,
      topRightLon: bounds?.[1]?.[1] ?? null,
    };
    const newCriteria = { ...searchCriteria, map };
    const res = await urlMaker(newCriteria, "maps");
    getListingsAndUpdateUrl(res.path);
  };

  const debouncedHandleMapBoundsChange = debounce(handleMapBoundsChange, 200);

  const focusCenterOfSelectedPolygons = async (bounds) => {
    const { topRight, bottomLeft } = bounds;
    let rectangle;

    if (topRight.lon) {
      rectangle = [
        [bottomLeft.lon, bottomLeft.lat],
        [topRight.lon, topRight.lat],
      ];
    } else {
      rectangle = window.ymaps.util.bounds.fromBounds([[bottomLeft, topRight]]);
    }

    const mapWidth = document.getElementById("listingmap").offsetWidth;
    const mapHeight = document.getElementById("listingmap").offsetHeight;
    const centerAndZoom = window.ymaps.util.bounds.getCenterAndZoom(rectangle, [mapWidth, mapHeight]);
    window.map.setCenter(centerAndZoom.center, centerAndZoom.zoom);
  };

  const getPOIs = async (poiTypes) => {
    const locations = getNodeLocations(searchCriteria);
    const selectedLocations = locations[0];
    const townId = selectedLocations?.town?.id ?? selectedLocations?.id;
    const hasPoiTypes = poiTypes.length > 0;
    if (!townId || !hasPoiTypes) return;
    await getPoisByTownId(townId, poiTypes).then((response) => {
      if (response[0]?.pois.length > 0) {
        removePOIs(window.map);
        setTimeout(() => {
          placePOIs(window.map, response);
        }, 250);
      }
    });
  };

  const placePOIs = (map, data) => {
    data?.map((poiData) => {
      poiData?.pois.map((poi) => {
        const lat = poi?.center?.lat;
        const lon = poi?.center?.lon;
        const title = poi?.name;
        const categoryId = poi?.categoryId;
        const poiType = poiTypes.filter((poiType) => poiType.id === categoryId);

        const placemark = new window.ymaps.Placemark(
          [lat, lon],
          {
            hintContent: title,
          },
          {
            iconLayout: "default#image",
            iconImageHref: poiType[0].placemarkImageHref,
            iconImageSize: [23, 31],
            hideIconOnBalloonOpen: false,
          }
        );

        filteredPOIs.push(placemark);

        return map.geoObjects.add(placemark);
      });
    });
    setFilteredPOIs(filteredPOIs);
  };

  const removePOIs = (map) => {
    filteredPOIs.map((filteredPOI) => map.geoObjects.remove(filteredPOI));
  };

  const removeMarkers = (map) => {
    currentPins.forEach((placemark) => {
      map.geoObjects.remove(placemark);
    });
    setCurrentPins([]);
  };

  const fetchListingsSubFunction = (searchCriteria) => {
    let searchedLocations = getNodeLocations(searchCriteria);
    if (selectedRegion) searchedLocations = [selectedRegion];
    if (searchedLocations.length === 0 && !selectedRegion) {
      placeMarkers(window.map);
      // If this is empty search focus back to Istanbul
      const currentURL = window.location.href;
      if (!getQueryParameterFromUrl("bottom_left", currentURL)) {
        const { bottomLeft, topRight } = initialBounds;
        window?.map?.setBounds([bottomLeft, topRight]);
      }
    } else {
      const reducedLocations = removeParentLocations(searchedLocations);
      clearAndFocusNewArea(window.map, reducedLocations);
    }
  };

  const clearAndFocusNewArea = async (map, locations) => {
    !boundsChangeRef.current && map.geoObjects.removeAll();
    placeMarkers(map);
    const activeLocation = decideLocationToFocus(locations);
    if (activeLocation) {
      const filterLocations = locations.length > 1 ? locations.filter((i) => i.type !== "city") : locations;
      await setCurrentLocationPolygons(map, filterLocations);
      await getPOIs(selectedPoiTypeIds);
    } else {
      setInitialLocation(map);
    }
  };

  const placeMarkers = (map, listings) => {
    removeMarkers(map);
    const placemarkListings = listings || allListings;
    const newPlacemarks = [];
    const viewedListings = localStorage.getItem("viewedListings");
    const placemarkData = [];
    // for loop was used because it was big data.
    for (let i = 0; i < placemarkListings.length; i++) {
      const listingItem = placemarkListings[i];
      const { tradeType, lat, lon, title, price, isOutstanding, isProjectItem } = getListingParams(listingItem);
      const placemarkClassNames = getPlacemarkClass(viewedListings, listingItem);
      const balloonContent = createBalloons({ isProjectItem, listingItem });
      const iconContent = getIconContent({
        placemarkClassNames,
        title,
        price,
        isProjectItem,
        tradeType,
        isOutstanding,
      });

      window.showPhoneNumber = (event, phoneNumber) => {
        if (event.target.innerHTML.includes("Telefona Bak")) {
          event.target.innerHTML = event.target.innerHTML.replace("Telefona Bak", phoneNumber);
        } else {
          window.open(`tel:${phoneNumber}`);
        }
      };

      const placemark = createPlacemark({
        maps: window.ymaps,
        lat,
        lon,
        iconContent,
        balloonContent,
        id: listingItem.id,
      });

      let balloonTimeout;
      let isHoveringOnBalloon = false;

      placemark.events.add("mouseenter", () => {
        clearTimeout(balloonTimeout);
        balloonTimeout = setTimeout(() => placemark.balloon.open(), 100);
        placemark.properties.set("iconContent", getPlacemarkHoverStyle(placemark));
      });

      placemark.events.add("mouseleave", () => {
        clearTimeout(balloonTimeout);
        balloonTimeout = setTimeout(() => {
          if (!isHoveringOnBalloon) placemark.balloon.close();
        }, 100);
        placemark.properties.set("iconContent", getPlacemarkNormalStyle(placemark, listingItem.id));
      });

      placemark.events.add("click", (e) => {
        e.preventDefault();
        e.stopPropagation();
        handlePlacemarkClick(listingItem.id);
      });

      placemark.balloon.events.add("mouseenter", () => {
        isHoveringOnBalloon = true;
        placemark.properties.set("iconContent", getPlacemarkHoverStyle(placemark));
      });

      placemark.balloon.events.add("mouseleave", () => {
        isHoveringOnBalloon = false;
        setTimeout(() => {
          placemark.balloon.close();
          placemark.properties.set("iconContent", getPlacemarkNormalStyle(placemark, listingItem.id));
        }, 100);
      });

      placemark.balloon.events.add("click", () => {
        const content = placemark.properties._data.iconContent;
        if (!content.includes("placemarkBefore") && !content.includes("visited")) {
          const visitedIcon = `<div class="placemark visited">${getMarkerContent(
            title,
            price,
            isProjectItem,
            tradeType,
            isOutstanding
          )}</div>`;
          placemark.properties.set("iconContent", visitedIcon);

          const updatedPlacemark = { id: listingItem.id, placemark, iconContent: visitedIcon };
          setPlacemarks([...placemarks, updatedPlacemark]);
        }

        localStorage.setItem("viewedListings", `${viewedListings},${listingItem.id}`);
      });

      map.events.add("mouseenter", () => {
        if (!placemark.balloon.isOpen() && !isHoveringOnBalloon) {
          placemark.balloon.close();
        }
      });

      placemarkData.push({ id: listingItem.id, placemark, iconContent });
      newPlacemarks.push(placemark);
      map.geoObjects.add(placemark);
    }

    setCurrentPins(newPlacemarks);
    setPlacemarks(placemarkData);
  };

  const handlePoiTypeChange = (poiType) => {
    const poiTypeIds = poiType.map(({ id }) => id);
    setSelectedPoiTypeIds(poiTypeIds);

    if (poiTypeIds.length > 0) {
      getPOIs(poiTypeIds);
    } else {
      removePOIs(window.map);
    }
  };

  return (
    <>
      {loading && <Loading />}
      <div className={styles.buttons}>
        <MapSwitchButton url={url} />
        {showPoi && <PoiButton visiblePoiTypes={visiblePoiTypes} onChange={handlePoiTypeChange} />}
      </div>
      <ListingCount count={total} />
      <div id="listingmap" ref={mapRef} style={{ width: "100%", height: "100%" }} />;
    </>
  );
};

ListingMap.propTypes = {
  criteria: PropTypes.object,
  listing: PropTypes.object,
  handlePlacemarkClick: PropTypes.func,
  url: PropTypes.object,
  allListings: PropTypes.array,
  listingsCount: PropTypes.number,
  getAllListings: PropTypes.func,
  newSearchResults: PropTypes.object,
  isProjectPage: PropTypes.bool,
};

export default ListingMap;
